import { SEARCH_RANKING_V2, } from "./catalog-search.js";
const { rrfK, weights, boost, editorialScale, freshness, descriptive } = SEARCH_RANKING_V2;
const DAY_MS = 86_400_000;
export const vectorCutoffOf = (modelKey) => modelKey === null ? null : (SEARCH_RANKING_V2.vectorCutoff[modelKey] ?? null);
/**
 * The least similarity at which a vector hit counts for this query: the larger of the absolute least and, when
 * the baseline has enough items, the median plus the margin.
 */
export function vectorSimilarityFloor(cutoff, baseline) {
    const relative = baseline !== null && baseline.count >= cutoff.minEmbedded
        ? baseline.medianSimilarity + cutoff.marginOverMedian
        : Number.NEGATIVE_INFINITY;
    return Math.max(cutoff.minSimilarity, relative);
}
/** A query of `descriptive.minTokens` words or more (stop words do not count) describes what the user wants. */
export const isDescriptiveQuery = (prepared) => (prepared.text?.queryTokens.length ?? 0) >= descriptive.minTokens;
/** The vector hits at or above `floor`, ranked again from 1 in their old order. */
function vectorHitsAbove(vector, floor) {
    return [...vector]
        .sort((a, b) => a.rank - b.rank)
        .filter((hit) => hit.similarity >= floor)
        .map((hit, index) => ({ ...hit, rank: index + 1 }));
}
/** 1 up to 180 days since the last verification, then linear to 0 at 540 days; never verified is 0. */
export function freshnessOf(lastVerifiedAt, now) {
    if (lastVerifiedAt === null)
        return 0;
    const days = (now.getTime() - lastVerifiedAt.getTime()) / DAY_MS;
    if (days <= freshness.fullDays)
        return 1;
    if (days >= freshness.zeroDays)
        return 0;
    return (freshness.zeroDays - days) / (freshness.zeroDays - freshness.fullDays);
}
/** `editorial_priority / 1000`, held between 0 and 1. */
export const editorialOf = (priority) => Math.min(1, Math.max(0, priority / editorialScale));
const ranksOf = (rows) => new Map(rows.map((row) => [row.id, row.rank]));
/**
 * Fuses the signals of one search. Candidates:
 *   - with text: the items the name tiers, the text words or the vector found (distance only ranks them);
 *   - with text and no name or word match: `noTextMatch`, and the list is the vector's, else the distance's,
 *     else the editorial order (a non-empty eligible set is not a zero result);
 *   - without text: the nearest items with `near`, else the editorial order.
 * An exact name (tier 1) sorts before every other hit, then `final` decides. Ties break by id, so the order is
 * total.
 *
 * `vectorCutoff` is the cut-off of the embedding model (`vectorCutoffOf`): a vector hit below
 * `vectorSimilarityFloor` is dropped, and the others are ranked again. `null` keeps every vector hit. When nothing
 * is left, the list is the one a query with no vector signal gets (see the rules above).
 */
export function rankCatalogSearch(input) {
    const { prepared, signals, now } = input;
    const nameTier = new Map(signals.name.map((row) => [row.id, row.tier]));
    const lexical = ranksOf(signals.lexical);
    const cutoff = input.vectorCutoff ?? null;
    const vector = ranksOf(cutoff === null
        ? signals.vector
        : vectorHitsAbove(signals.vector, vectorSimilarityFloor(cutoff, signals.vectorBaseline)));
    // The descriptive weights exist to weigh the vector against the words. They apply only when a vector list is left
    // to weigh: with no query vector (embedder off, UNAVAILABLE, no text), and when the cut-off removed every vector
    // hit, the plain v1 weights keep the order of the lexical-only path exactly.
    const textWeights = isDescriptiveQuery(prepared) && vector.size > 0 ? descriptive.weights : weights;
    const geo = ranksOf(signals.geo);
    const editorial = ranksOf(signals.editorial);
    const ids = new Set();
    let noTextMatch = false;
    if (prepared.text !== null) {
        for (const id of nameTier.keys())
            ids.add(id);
        for (const id of lexical.keys())
            ids.add(id);
        noTextMatch = ids.size === 0;
        for (const id of vector.keys())
            ids.add(id);
        if (ids.size === 0) {
            for (const id of (geo.size > 0 ? geo : editorial).keys())
                ids.add(id);
        }
    }
    else {
        for (const id of (prepared.near !== null ? geo : editorial).keys())
            ids.add(id);
    }
    const wanted = prepared.preferenceTags.length;
    const candidates = [];
    for (const id of ids) {
        const attributes = signals.attributes.get(id);
        if (attributes === undefined)
            continue;
        const tier = nameTier.get(id) ?? null;
        const lexicalRank = lexical.get(id) ?? null;
        const vectorRank = vector.get(id) ?? null;
        const geoRank = geo.get(id) ?? null;
        let fused = 0;
        if (tier !== null)
            fused += weights.name / (rrfK + tier);
        if (lexicalRank !== null)
            fused += textWeights.lexical / (rrfK + lexicalRank);
        if (vectorRank !== null)
            fused += textWeights.vector / (rrfK + vectorRank);
        if (geoRank !== null)
            fused += weights.geo / (rrfK + geoRank);
        if (tier === null && lexicalRank === null && vectorRank === null && geoRank === null) {
            // Only the fallback order ranks it.
            const rank = editorial.get(id);
            if (rank !== undefined)
                fused += SEARCH_RANKING_V2.fallbackEditorialWeight / (rrfK + rank);
        }
        const editorialBoost = boost.editorial * editorialOf(attributes.editorialPriority);
        const freshnessBoost = boost.freshness * freshnessOf(attributes.lastVerifiedAt, now);
        const preferenceBoost = wanted === 0 ? 0 : boost.preference * (Math.min(attributes.preferenceHits, wanted) / wanted);
        const final = fused * (1 + editorialBoost + freshnessBoost + preferenceBoost);
        const reasons = [];
        if (tier === 1)
            reasons.push("NAME_EXACT");
        else if (tier === 2)
            reasons.push("NAME_PHRASE");
        else if (tier === 3)
            reasons.push("NAME_PREFIX");
        if (lexicalRank !== null)
            reasons.push("TEXT_MATCH");
        if (vectorRank !== null)
            reasons.push("SEMANTIC_MATCH");
        if (prepared.near !== null)
            reasons.push("WITHIN_RADIUS");
        if (editorialBoost > 0)
            reasons.push("EDITORIAL_BOOST");
        if (freshnessBoost > 0)
            reasons.push("FRESHNESS_BOOST");
        if (preferenceBoost > 0)
            reasons.push("PREFERENCE_BOOST");
        candidates.push({
            attributes,
            scores: {
                final,
                fused,
                nameTier: tier,
                lexicalRank,
                vectorRank,
                geoRank,
                editorialBoost,
                freshnessBoost,
                preferenceBoost,
            },
            reasons,
        });
    }
    // The tier-1 rule of the ORDER: an exact name (the folded name equals the folded query) sorts before any hit
    // that is not an exact name, then by `final` as for all. A business boost must not reorder name tiers, and
    // the gap between a tier 1 and a tier 2 name hit is small (`2/61 - 2/62`), so a +10 % boost could win it.
    // Only the order has the rule: the scores (`final`, `fused`, `nameTier`) are not changed. Tier 2 and tier 3
    // have no floor. The rule is part of ranking v1 and v2.
    const exactFirst = (candidate) => (candidate.scores.nameTier === 1 ? 0 : 1);
    candidates.sort((a, b) => exactFirst(a) - exactFirst(b) ||
        b.scores.final - a.scores.final ||
        b.scores.fused - a.scores.fused ||
        (a.attributes.id < b.attributes.id ? -1 : a.attributes.id > b.attributes.id ? 1 : 0));
    return { candidates, noTextMatch };
}
/** The most hits one item type and locality may have: `max(3, ceil(limit / 3))`. */
export const diversityCap = (limit) => Math.max(SEARCH_RANKING_V2.diversity.minPerGroup, Math.ceil(limit / SEARCH_RANKING_V2.diversity.groupDivisor));
/** The key a pair of ids is looked up by (order does not matter). */
export const pairKey = (a, b) => (a < b ? `${a}|${b}` : `${b}|${a}`);
/**
 * Picks the shortlist, greedily in `final` order. A listing that is a near-duplicate of a hit already picked
 * (same item type, same locality, and the same name or a vector cosine of at least 0.97) folds into it. At
 * most `diversityCap(limit)` hits share an item type and locality, unless the request pins one type and one
 * locality. `similar` holds `pairKey`s of pairs whose cosine reached the threshold.
 */
export function diversifyCatalogSearch(input) {
    const cap = diversityCap(input.limit);
    const hits = [];
    const perGroup = new Map();
    const groupOf = (candidate) => `${candidate.attributes.itemType}|${candidate.attributes.localityKey ?? ""}`;
    for (const candidate of input.ranked) {
        const { attributes } = candidate;
        const group = groupOf(candidate);
        const twin = hits.find((hit) => groupOf(hit.candidate) === group &&
            (hit.candidate.attributes.nameKey === attributes.nameKey ||
                input.similar.has(pairKey(hit.candidate.attributes.id, attributes.id))));
        if (twin !== undefined) {
            twin.collapsed.push({
                id: attributes.id,
                name: attributes.name,
                reason: twin.candidate.attributes.nameKey === attributes.nameKey ? "SAME_NAME" : "SIMILAR_VECTOR",
            });
            continue;
        }
        if (!input.pinned && (perGroup.get(group) ?? 0) >= cap)
            continue;
        if (hits.length >= input.limit)
            continue;
        perGroup.set(group, (perGroup.get(group) ?? 0) + 1);
        hits.push({ candidate, collapsed: [] });
    }
    return hits;
}
